import { PreofferDetail, PreofferSummary } from '@data/interfaces/preoffer.interface'

export type PreofferTone = 'ok' | 'warn' | 'bad' | 'info' | 'neutral'

/** Cómo se pinta una preoferta en la píldora y en la tarjeta (plan v4.3 §9.3 y §2b). Puro: se prueba sin TestBed. */
export interface PreofferView {
  label: string
  tone: PreofferTone
  //* Borde punteado: lo decidido en sombra, que el cliente nunca vio.
  dashed: boolean
  lines: string[]
  canDownload: boolean
  canResend: boolean
}

const MXN = (amount: number) => `$${Math.round(Number(amount) || 0).toLocaleString('es-MX')} MXN`
const DATE = (value: string | Date) => new Date(value).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'America/Mexico_City' })
const DAY_MS = 24 * 60 * 60 * 1000

export const PREOFFER_TRIGGERS: Record<string, string> = {
  ciec_completed: 'CIEC completada',
  sweep: 'foto asentada',
  guarantors_changed: 'cambio de obligados',
  manual: 'reevaluación manual',
}

//* D-76 · Solo para el equipo: la carta salió por el tope y el cliente da para más.
const cappedLine = (capped: boolean, capacity: number | null) => (capped && capacity ? [`Salió por el tope · capacidad estimada ${MXN(capacity)}`] : [])

const noticeLine = (notice: PreofferSummary['sellerNotice']) => {
  if (!notice || notice.status === 'not_applicable') return []
  if (notice.status === 'sent') return [`Aviso al comercial enviado${notice.sentAt ? ` el ${DATE(notice.sentAt)}` : ''}`]
  if (notice.status === 'failed') return ['El aviso al comercial no salió']
  return ['Aviso al comercial en cola']
}

export function preofferView(preoffer: PreofferSummary | PreofferDetail | null | undefined, now: Date = new Date()): PreofferView | null {
  if (!preoffer) return null
  const detail = preoffer as PreofferDetail
  const reasons = (preoffer.reasons ?? []).map((reason) => reason.label)
  const base = { dashed: false, canDownload: false, canResend: false }

  switch (preoffer.status) {
    case 'analyzing': {
      const pending = detail.pendingSources
      return { ...base, label: 'Analizando', tone: 'neutral', lines: [pending ? `Faltan ${pending} ${pending === 1 ? 'fuente' : 'fuentes'} de la foto` : 'Esperando la foto de Syntage'] }
    }
    case 'not_eligible':
      return { ...base, label: 'En revisión', tone: 'warn', lines: reasons.length ? reasons : ['Sin motivo registrado'] }
    case 'legal_review': {
      const since = detail.legalSince ? `Esperando autorización de Legal desde el ${DATE(detail.legalSince)}` : 'Esperando autorización de Legal'
      const signals = (preoffer.legalSignals ?? []).map((signal) => `${signal.label} (${signal.count})`)
      const trigger = detail.trigger && PREOFFER_TRIGGERS[detail.trigger] ? [`Disparador: ${PREOFFER_TRIGGERS[detail.trigger]}`] : []
      return { ...base, label: 'Con Legal', tone: 'info', lines: [since, ...signals, ...trigger] }
    }
    case 'issued': {
      const offer = preoffer.offer
      if (!offer) return { ...base, label: 'Emitida', tone: 'ok', lines: [] }
      const days = Math.ceil((new Date(offer.validUntil).getTime() - now.getTime()) / DAY_MS)
      const terms = offer.termMonths && offer.monthlyPayment ? [`${offer.termMonths} meses · pago estimado ${MXN(offer.monthlyPayment)}`] : []
      const lines = [
        `${MXN(offer.amount)} preaprobados, IVA incluido`,
        ...terms,
        ...cappedLine(offer.capped, offer.capacity),
        days > 0 ? `Vence el ${DATE(offer.validUntil)} (${days === 1 ? 'falta 1 día' : `faltan ${days} días`})` : `Venció el ${DATE(offer.validUntil)}`,
      ]
      const delivery = preoffer.delivery
      if (delivery?.status === 'sent' && delivery.sentAt) lines.push(`Último envío: ${DATE(delivery.sentAt)}`)
      else if (delivery?.status === 'pending') lines.push('Correo en cola de envío')
      else if (delivery?.status === 'failed') lines.push(`El correo no salió${delivery.lastError ? `: ${delivery.lastError}` : ''}`)
      lines.push(...noticeLine(preoffer.sellerNotice))
      return { ...base, label: days > 0 ? 'Emitida' : 'Vencida', tone: days > 0 ? 'ok' : 'neutral', lines, canDownload: preoffer.hasDocument, canResend: days > 0 && preoffer.mode === 'live' }
    }
    case 'returned': {
      const review = preoffer.review
      const lines = [review?.reason ? `Motivo: ${review.reason}` : 'Sin motivo registrado']
      if (review?.at) lines.push(`Regresada el ${DATE(review.at)}`)
      return { ...base, label: 'Regresada por Legal', tone: 'bad', lines }
    }
    case 'expired':
      return { ...base, label: 'Vencida', tone: 'neutral', lines: preoffer.offer ? [`Venció el ${DATE(preoffer.offer.validUntil)}`] : [] }
    case 'withdrawn':
      return { ...base, label: 'Retirada', tone: 'neutral', lines: ['Comité o cancelación de un arrendamiento del cliente'] }
    case 'shadow': {
      const amount = Number(preoffer.expectedAmount) || 0
      const outcome =
        preoffer.shadowOutcome === 'issued'
          ? `Habría salido por ${MXN(amount)}${preoffer.capped ? ' (el tope)' : ''} · no se envió (modo sombra)`
          : preoffer.shadowOutcome === 'legal_review'
            ? 'Habría ido a la bandeja de Legal · no se envió (modo sombra)'
            : 'Habría quedado en revisión (modo sombra)'
      return { ...base, label: 'Sombra', tone: 'info', dashed: true, lines: preoffer.shadowOutcome === 'not_eligible' ? [outcome, ...reasons] : [outcome] }
    }
    default:
      return null
  }
}
